import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    createFunnel,
    deleteFunnel,
    getAppVersions,
    getFunnelDiscount,
    getFunnel,
    getFunnelElementTypes,
    getFunnelOptions,
    getHtmlTemplates,
    getStaticOfferAttractivity,
    getTemplates,
    getUserTags,
    putFunnel
} from '../Api';
import {useNavigate, useParams} from 'react-router-dom';
import TemplateDto from '../models/dtos/TemplateDto';
import FunnelConfigDto from '../models/dtos/FunnelConfigDto';
import TierInfoDto from '../models/dtos/TierInfoDto';
import FunnelOptions from '../models/dtos/FunnelOptions';
import FunnelConfigElementDto, {
    Constraints,
    FunnelConfigElementBetcoinsDto
} from "../models/dtos/FunnelConfigElementDto";
import HtmlTemplateDto from '../models/dtos/HtmlTemplateDto';
import AppVersionDto from '../models/dtos/AppVersionDto';
import StaticOfferAttractivityDto from '../models/dtos/StaticOfferAttractivityDto';
import HtmlTemplatePreview from '../components/HtmlTemplatePreview';
import AndroidIcon from '@mui/icons-material/Android';
import {v4 as uuidv4} from 'uuid';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import { UserTagCombinationMode } from '../models/UserTagCombinationMode';

const IosLogo = ({size = 20}: {size?: number}) => <img src="/ios_logo.png" alt="iOS" style={{width: size, height: size, objectFit: 'contain'}}/>;

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it', 'ro', 'ru', 'es', 'pt', 'pl', 'tr'];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_BUILD_VERSION = 1_000_000_000;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250,
        },
    },
};

export interface Cancelable {
    clear(): void;
}

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency', currency: 'USD',
    });
}

const getOsTypeLabel = (osType: string | null | undefined) => {
    switch (osType) {
        case 'ios':
            return 'iOS';
        case 'android':
            return 'Android';
        default:
            return osType ?? '';
    }
}

const sanitizeBuildVersionInput = (value: string) => value.replace(/[^\d]/g, '');

type RewardConfiguration = 'STATIC' | 'DYNAMIC';

export default function AddFunnelPage() {
    let id = useParams()['id'] as string
    let isEditMode = id != undefined;

    const [state, setState] = React.useState({
        isEditMode: isEditMode,
        funnelPresent: false,
        dialogOpen: false,
        dialogMsg: '',
        templates: [] as TemplateDto[],
        htmlTemplates: [] as HtmlTemplateDto[],
        funnel: {
            activationEvents: [] as string[],
            lifetime: 'SixHours',
            cooldownTime: 'TwelveHours',
            funnelType: 'Simple',
            bonusSize: 0.5,
            enabled: true,
            dailyLimit: 100,
            elements: [] as FunnelConfigElementDto[],
            offerType: "DYNAMIC",
            osTypes: ['android'] as string[],
            customBetcoins: null,
            oldPriceTier: null,
            minBuildNumber: null,
            maxBuildNumber: null,
            htmlTemplateId: null,
            userPurchaserType: 'ALL',
            userTags: [] as string[],
            userTagCombinationMode: 'ALL' as UserTagCombinationMode,
            useForSupport: false,
        } as FunnelConfigDto,
        options: {
            tiers: [] as TierInfoDto[],
            funnelTypes: [] as string[],
            offerTypes: [] as string[],
            scopes: [] as string[],
            activationEvents: [] as string[],
            osTypes: [] as string[],
            userPurchaserTypes: [] as string[],
            userTagCombinationModes: [] as UserTagCombinationMode[],
        } as FunnelOptions,
        funnelElementTypes: [] as string[]
    });
    const navigate = useNavigate()
    const [templateMode, setTemplateMode] = React.useState<'legacy' | 'html'>('legacy');
    const [previewLanguage, setPreviewLanguage] = React.useState('en');
    const [appVersions, setAppVersions] = React.useState<AppVersionDto[]>([]);
    const [appVersionsLoading, setAppVersionsLoading] = React.useState(false);
    const [minBuildNumberInput, setMinBuildNumberInput] = React.useState('');
    const [maxBuildNumberInput, setMaxBuildNumberInput] = React.useState('');
    const [availableUserTags, setAvailableUserTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [rewardConfiguration, setRewardConfiguration] = React.useState<RewardConfiguration>('DYNAMIC');
    const [discountValue, setDiscountValue] = React.useState<number | null>(null);
    const [discountLoading, setDiscountLoading] = React.useState(false);
    const [staticOfferAttractivity, setStaticOfferAttractivity] = React.useState<StaticOfferAttractivityDto | null>(null);
    const [staticOfferAttractivityLoading, setStaticOfferAttractivityLoading] = React.useState(false);

    React.useEffect(() => {

        const fetchData = async () => {
            if (isEditMode) {
                let [funnel, options, templates, funnelElementTypes, htmlTemplates, userTags] = await Promise.all([getFunnel(id), getFunnelOptions(), getTemplates(), getFunnelElementTypes(), getHtmlTemplates(), getUserTags()]);
                const normalizedFunnel = {
                    ...funnel,
                    elements: funnel.elements || [],
                    osTypes: funnel.osTypes?.length ? funnel.osTypes : ['android'],
                    oldPriceTier: funnel.oldPriceTier ?? null,
                    minBuildNumber: funnel.minBuildNumber ?? null,
                    maxBuildNumber: funnel.maxBuildNumber ?? null,
                    userPurchaserType: funnel.userPurchaserType ?? 'ALL',
                    userTags: funnel.userTags ?? [],
                    userTagCombinationMode: funnel.userTagCombinationMode ?? 'ALL',
                };
                setState(prevState => ({
                    ...prevState,
                    funnel: normalizedFunnel,
                    options: options,
                    templates: templates,
                    htmlTemplates: htmlTemplates,
                    funnelPresent: true,
                    funnelElementTypes: funnelElementTypes
                }));
                setRewardConfiguration(normalizedFunnel.customBetcoins != null ? 'STATIC' : 'DYNAMIC');
                setAvailableUserTags((userTags ?? []).slice().sort((left, right) => left.tagName.localeCompare(right.tagName)));
                setMinBuildNumberInput(normalizedFunnel.minBuildNumber != null ? String(normalizedFunnel.minBuildNumber) : '');
                setMaxBuildNumberInput(normalizedFunnel.maxBuildNumber != null ? String(normalizedFunnel.maxBuildNumber) : '');
                if (funnel.htmlTemplateId) {
                    setTemplateMode('html');
                }
                for (let i = 0; i < (funnel.elements?.length ?? 0); i++) {
                    getMatchingConstraints(funnel.elements[i].layoutParams.constraints, i);
                }
            } else {
                let [options, templates, funnelElementTypes, htmlTemplates, userTags] = await Promise.all([getFunnelOptions(), getTemplates(), getFunnelElementTypes(), getHtmlTemplates(), getUserTags()]);
                setState(prevState => ({
                    ...prevState,
                    options: options,
                    templates: templates,
                    htmlTemplates: htmlTemplates,
                    funnelPresent: true,
                    funnelElementTypes: funnelElementTypes
                }));
                setAvailableUserTags((userTags ?? []).slice().sort((left, right) => left.tagName.localeCompare(right.tagName)));
            }

        }

        fetchData();
    }, [])

    const selectedOsType = state.funnel.osTypes?.[0] ?? '';
    const isStaticReward = rewardConfiguration === 'STATIC';
    const selectedUserTagOptions = state.funnel.userTags.map(tagName => (
        availableUserTags.find(tag => tag.tagName === tagName) ?? {
            id: tagName,
            tagName,
            requirements: [],
            approximateUsersCount: null,
            tagRecalculationInfo: null,
            createdAt: '',
        }
    ));

    React.useEffect(() => {
        const fetchAppVersions = async () => {
            if (!selectedOsType) {
                setAppVersions([]);
                return;
            }

            setAppVersionsLoading(true);
            const versions = await getAppVersions(selectedOsType);
            setAppVersions(versions.sort((a, b) => a.buildNumber - b.buildNumber));
            setAppVersionsLoading(false);
        };

        fetchAppVersions();
    }, [selectedOsType]);

    React.useEffect(() => {
        if (!isStaticReward || !selectedOsType || !state.funnel.minTier) {
            setDiscountValue(null);
            setDiscountLoading(false);
            return;
        }

        let active = true;
        setDiscountLoading(true);

        getFunnelDiscount(
            state.funnel.minTier,
            selectedOsType,
            isStaticReward ? (state.funnel.oldPriceTier ?? null) : null
        ).then(result => {
            if (!active) {
                return;
            }

            setDiscountValue(result);
            setDiscountLoading(false);
        }).catch(() => {
            if (!active) {
                return;
            }

            setDiscountValue(null);
            setDiscountLoading(false);
        });

        return () => {
            active = false;
        };
    }, [isStaticReward, selectedOsType, state.funnel.minTier, state.funnel.oldPriceTier]);

    React.useEffect(() => {
        if (!isStaticReward || !selectedOsType || !state.funnel.minTier || state.funnel.customBetcoins == null || state.funnel.customBetcoins <= 0) {
            setStaticOfferAttractivity(null);
            setStaticOfferAttractivityLoading(false);
            return;
        }

        let active = true;
        setStaticOfferAttractivityLoading(true);

        getStaticOfferAttractivity(
            state.funnel.minTier,
            state.funnel.customBetcoins,
            selectedOsType,
        ).then(result => {
            if (!active) {
                return;
            }

            setStaticOfferAttractivity(result);
            setStaticOfferAttractivityLoading(false);
        }).catch(() => {
            if (!active) {
                return;
            }

            setStaticOfferAttractivity(null);
            setStaticOfferAttractivityLoading(false);
        });

        return () => {
            active = false;
        };
    }, [isStaticReward, selectedOsType, state.funnel.minTier, state.funnel.customBetcoins]);

    const getSelectedBuildVersionOption = (value: number | null | undefined) => {
        if (value == null) return null;
        return appVersions.find(option => option.buildNumber === value) ?? null;
    };

    const getBuildVersionHintLabel = (value: number | null | undefined) => {
        if (value == null) return null;
        const matchingVersion = getSelectedBuildVersionOption(value);
        return matchingVersion ? `${matchingVersion.buildVersion} (${matchingVersion.buildNumber})` : `${value}`;
    };

    const minBuildVersionOptions = appVersions.filter(option => state.funnel.maxBuildNumber == null || option.buildNumber <= state.funnel.maxBuildNumber);
    const maxBuildVersionOptions = appVersions.filter(option => state.funnel.minBuildNumber == null || option.buildNumber >= state.funnel.minBuildNumber);

    const minBuildVersionError = state.funnel.minBuildNumber != null &&
        (state.funnel.minBuildNumber <= 0
            || state.funnel.minBuildNumber >= MAX_BUILD_VERSION
            || (state.funnel.maxBuildNumber != null && state.funnel.minBuildNumber > state.funnel.maxBuildNumber));

    const maxBuildVersionError = state.funnel.maxBuildNumber != null &&
        (state.funnel.maxBuildNumber <= 0
            || state.funnel.maxBuildNumber >= MAX_BUILD_VERSION
            || (state.funnel.minBuildNumber != null && state.funnel.maxBuildNumber < state.funnel.minBuildNumber));
    const pricingTierLabel = isStaticReward ? 'Pricing tier' : 'Min pricing tier';
    const pricingTierHint = isStaticReward
        ? "This is the pricing tier for the offer. Exact value depends on the currency/region"
        : "This is minimal allowed pricing tier. Exact one will be inferred basing on user's average purchase amount";
    const formattedDiscount = discountValue == null ? null : `${Math.round(discountValue * 100)}%`;
    const formattedAttractivity = staticOfferAttractivity == null
        ? null
        : `${staticOfferAttractivity.benefitPercent > 0 ? '+' : ''}${Math.round(staticOfferAttractivity.benefitPercent * 10) / 10}%`;

    const getAttractivityColor = (verdict: string | undefined) => {
        if (verdict == null) {
            return 'text.secondary';
        }

        switch (verdict) {
            case 'FANTASTIC':
            case 'GREAT':
            case 'GOOD':
                return 'success.main';
            case 'FAIR':
                return 'warning.main';
            default:
                return 'error.main';
        }
    };

    const deviceRestrictionsHint = React.useMemo(() => {
        if (!selectedOsType) return '';

        const parts = [] as string[];
        if (state.funnel.minBuildNumber != null) {
            parts.push(`min build version ${getBuildVersionHintLabel(state.funnel.minBuildNumber)}`);
        }
        if (state.funnel.maxBuildNumber != null) {
            parts.push(`max build version ${getBuildVersionHintLabel(state.funnel.maxBuildNumber)}`);
        }

        const baseText = `Funnel will only emit offers for users of ${getOsTypeLabel(selectedOsType)}`;
        return parts.length === 0 ? `${baseText}.` : `${baseText} with ${parts.join(' and ')}.`;
    }, [selectedOsType, state.funnel.minBuildNumber, state.funnel.maxBuildNumber, appVersions]);

    const applyBuildVersionInput = (field: 'minBuildNumber' | 'maxBuildNumber', value: string) => {
        const sanitizedValue = sanitizeBuildVersionInput(value);

        if (field === 'minBuildNumber') {
            setMinBuildNumberInput(sanitizedValue);
        } else {
            setMaxBuildNumberInput(sanitizedValue);
        }

        setState(prevState => ({
            ...prevState,
            funnel: {
                ...prevState.funnel,
                [field]: sanitizedValue === '' ? null : parseInt(sanitizedValue, 10)
            }
        }));
    };

    const onMinTierSelected = (minTier: string) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, minTier: minTier}}));
    }

    const onOldPriceTierSelected = (oldPriceTier: string) => {
        setState(prevState => ({
            ...prevState, funnel: {...prevState.funnel, oldPriceTier: oldPriceTier.length > 0 ? oldPriceTier : null}
        }));
    }

    const onOfferLifetimeSelected = (scope: string) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, lifetime: scope}}));
    }

    const onCooldowntimeSelected = (scope: string) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, cooldownTime: scope}}));
    }

    const onLocalizationTemplateSelected = (template: string) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, localizationTemplate: template}}));
    }

    const onCreateClicked = async () => {
        var hasError = false;
        let errorMsg = '';

        if (state.funnel.name == undefined || state.funnel.name.length < 3 || state.funnel.name.length > 25) {
            hasError = true;
            errorMsg = "No valid funnel name is specified! It should be between 3 and 25 characters";
        } else if (state.funnel.minTier == undefined || state.funnel.minTier.trim().length == 0) {
            hasError = true;
            errorMsg = "No valid pricing tier is selected!";
        } else if (isStaticReward && state.funnel.customBetcoins == null) {
            hasError = true;
            errorMsg = "Custom Betcoins is required for static rewards!";
        } else if (!isStaticReward && (state.funnel.bonusSize > 5.0 || state.funnel.bonusSize < 0)) {
            hasError = true;
            errorMsg = "No valid bonus size (between 0% and 500%) is selected!";
        } else if (templateMode === 'legacy' && (state.funnel.localizationTemplate == undefined || state.funnel.localizationTemplate.length == 0)) {
            hasError = true;
            errorMsg = "No valid localization template is selected!";
        } else if (templateMode === 'html' && (state.funnel.htmlTemplateId == undefined || state.funnel.htmlTemplateId == null || state.funnel.htmlTemplateId.length == 0)) {
            hasError = true;
            errorMsg = "No valid HTML template is selected!";
        } else if ((state.funnel.osTypes?.[0] ?? '').length === 0) {
            hasError = true;
            errorMsg = "OS type is required!";
        } else if (state.funnel.activationEvents.length == 0) {
            hasError = true;
            errorMsg = "Funnel should have at least 1 activation event!";
        } else if (state.funnel.dailyLimit < 0 && state.funnel.dailyLimit > 10000000) {
            hasError = true;
            errorMsg = "Funnel daily limit should be within 0 and 1000000";
        } else if (isStaticReward && state.funnel.customBetcoins != null && (state.funnel.customBetcoins < 0 || state.funnel.customBetcoins > 1000000000)) {
            hasError = true;
            errorMsg = "Custom Betcoins should be between 0 and 1,000,000,000";
        } else if (state.funnel.minBuildNumber != null && (state.funnel.minBuildNumber <= 0 || state.funnel.minBuildNumber >= MAX_BUILD_VERSION)) {
            hasError = true;
            errorMsg = "Min build version should be greater than 0 and less than 1,000,000,000";
        } else if (state.funnel.maxBuildNumber != null && (state.funnel.maxBuildNumber <= 0 || state.funnel.maxBuildNumber >= MAX_BUILD_VERSION)) {
            hasError = true;
            errorMsg = "Max build version should be greater than 0 and less than 1,000,000,000";
        } else if (state.funnel.minBuildNumber != null && state.funnel.maxBuildNumber != null && state.funnel.maxBuildNumber < state.funnel.minBuildNumber) {
            hasError = true;
            errorMsg = "Max build version should be greater than or equal to min build version";
        }

        if (!hasError) {
            let result = false;
            const funnelToSave = {
                ...state.funnel,
                funnelType: 'Simple',
                localizationTemplate: templateMode === 'legacy' ? state.funnel.localizationTemplate : 'None',
                htmlTemplateId: templateMode === 'html' ? state.funnel.htmlTemplateId : null,
                tierForNonPurchasers: state.funnel.minTier,
                oldPriceTier: isStaticReward ? (state.funnel.oldPriceTier ?? null) : null,
                bonusSize: isStaticReward ? 0 : state.funnel.bonusSize,
                customBetcoins: isStaticReward ? state.funnel.customBetcoins : null,
            };
            if (isEditMode) {
                result = await putFunnel(funnelToSave.id, funnelToSave);
            } else {
                result = await createFunnel(funnelToSave);
            }

            hasError = !result;

            if (!result) {
                errorMsg = "Server error!";
            }
        }

        if (hasError) {
            setState({...state, dialogMsg: errorMsg, dialogOpen: true});
        } else navigate('/funnels')
    }

    const onDeleteClicked = async () => {
        var hasError = false;
        let errorMsg = '';

        if (!hasError) {
            let result = await deleteFunnel(state.funnel.id);

            hasError = !result;

            if (!result) {
                errorMsg = "Server error!";
            }
        }

        if (hasError) {
            setState({...state, dialogMsg: errorMsg, dialogOpen: true});
        } else navigate('/funnels')
    }

    const editLocalizations = async () => {
        navigate('/funnels/localizations');
    }

    const [showBiasControls, setShowBiasControls] = React.useState(false);
    const [showMarginsControls, setShowMarginsControls] = React.useState(false);
    /*region Constraints*/
    const availableOptions = ['top', 'start', 'end', 'bottom'];

    const [constraints, setConstraints] = React.useState(Array.from({length: 16}, () => ({
        constraint1: '', constraint2: ''
    })) as {
        constraint1: string, constraint2: string
    }[])
    const handleConstraint1Change = (index: number, value: string, element_index: number) => {
        if (constraints[index].constraint2 !== '' && constraints[index].constraint1 !== '') {
            updateElementConstraint(element_index, constraints[index].constraint1, constraints[index].constraint2, null);
        }
        setConstraints(constraints => {
            const newConstraints = [...constraints];
            newConstraints[index] = {constraint1: value, constraint2: ''};
            return newConstraints;
        })
    };
    const getConstraint2Options = (constraint1: string) => {
        if (constraint1 === 'top' || constraint1 === 'bottom') {
            return ['top', 'bottom'];
        }
        return ['start', 'end'];
    };
    const handleConstraint2Change = (index: number, value: string) => {
        setConstraints(constraints => {
            const newConstraints = [...constraints];
            newConstraints[index].constraint2 = value;
            return newConstraints;
        })
    };

    const updateElementConstraint = (index: number, firstConstraint: string, secondConstraint: string, value: string | null) => {
        const formattedSecondConstraint = secondConstraint.charAt(0).toUpperCase() + secondConstraint.slice(1).toLowerCase();

        setState(prevState => ({
            ...prevState, funnel: {
                ...prevState.funnel, elements: prevState.funnel.elements.map((el, i) => i === index ? {
                    ...el, layoutParams: {
                        ...el.layoutParams, constraints: {
                            ...el.layoutParams.constraints,
                            [`${firstConstraint.toLowerCase()}To${formattedSecondConstraint}Id`]: value
                        }
                    }
                } : el) as FunnelConfigElementDto[]
            }
        }));
    }


    /*endregion*/
    const addElements = async () => {
        setState(prevState => ({
            ...prevState, funnel: {
                ...prevState.funnel, elements: [...prevState.funnel.elements, {
                    id: uuidv4(), layoutParams: {
                        constraints: {
                            verticalBias: 0.5,
                            horizontalBias: 0.5,
                            startToEndId: null,
                            endToStartId: null,
                            topToBottomId: null,
                            bottomToTopId: null,
                            startToStartId: null,
                            endToEndId: null,
                            topToTopId: null,
                            bottomToBottomId: null,
                        }, margins: {
                            top: 0, start: 0, end: 0, bottom: 0
                        }
                    }
                } as FunnelConfigElementDto]
            }
        }));
    }

    const handleDialogClose = () => {
        setState({...state, dialogOpen: false});
    }

    const handleActivationEventsChange = (event: SelectChangeEvent<string[]>) => {
        let events = [] as string[];
        if (typeof event.target.value == 'string') {
            events = event.target.value.split('');
        } else {
            events = event.target.value as string[];
        }
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, activationEvents: events}}));
    };

    const onEnabledChanged = async (enabled: boolean) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, enabled: enabled}}));
    }

    const onUserPurchaserTypeSelected = (userPurchaserType: string) => {
        setState(prevState => ({...prevState, funnel: {...prevState.funnel, userPurchaserType: userPurchaserType}}));
    }

    const getMatchingConstraints = (cons: Constraints, elementIndex: number) => {
        const regex = /^([a-z]+)To([A-Z][a-z]+)Id$/;
        let i = 0
        for (const key in cons) {
            const match = key.match(regex);
            if (match) {
                if (cons[key] != null) {
                    if (constraints.slice(elementIndex * 4, (elementIndex + 1) * 4).find((c) => c.constraint1 === match[1] && c.constraint2 === match[2].toLowerCase()) == null) {
                        constraints[elementIndex * 4 + i].constraint1 = match[1];
                        constraints[elementIndex * 4 + i].constraint2 = match[2].toLowerCase();
                        i++
                    }
                }
            }
        }
        return constraints
    };

    const renderElementSpecificFields = (element: FunnelConfigElementDto, index: number,) => {
        switch (element.type) {
            case 'Betcoins':

                return (<>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="customBetcoins" alignContent="center" alignItems='center'>
                        <TextField label="Custom Betcoins" type="number" variant="filled"
                                   value={(element as FunnelConfigElementBetcoinsDto).customBetcoins || ''}
                                   fullWidth={true}
                                   onChange={e => {

                                       const value: number | null = (e.target.value == '') ? null : parseInt(e.target.value);
                                       setState(prevState => ({
                                           ...prevState, funnel: {
                                               ...prevState.funnel,
                                               elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                   ...el as FunnelConfigElementBetcoinsDto, customBetcoins: value
                                               } : el)
                                           }
                                       }));

                                   }}
                                   inputProps={{step: 1, min: 0}}
                        />
                    </Grid>
                </>)
        }
    }
    return (<div>
        <Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>

            <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 6}} alignItems='center'>
                <Grid item xs={0} sm={0} md={1} lg={2}/>
                <Grid item xs={2}>
                    <Typography variant="h3">{isEditMode ? "Edit funnel" : "Add new funnel"}</Typography>
                </Grid>
                <Box width="100%"/>
                <Grid item xs={0} sm={0} md={1} lg={2}/>
                <Grid item xs={2}>
                    <Typography variant="h6">Funnel configuration:</Typography>
                </Grid>
                {!state.funnelPresent && state.isEditMode ? <React.Fragment>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="progress"><CircularProgress size={64}
                                                                       style={{marginBottom: '32px'}}/></Grid>
                </React.Fragment> : <React.Fragment>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="name">
                        <TextField label="name" variant="filled" value={state.funnel.name || ''} fullWidth={true}
                                   onChange={e => {
                                       setState(prevState => ({
                                           ...prevState, funnel: {...prevState.funnel, name: e.target.value}
                                       }));
                                   }}/>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={1} key="enabled" alignContent="center" alignItems='center'>
                        <FormControlLabel control={<Switch checked={state.funnel.enabled}
                                                           onChange={e => onEnabledChanged(e.target.checked)}/>}
                                          label="Enable funnel"/>
                    </Grid>
                    <Grid item xs={3} key="useForSupport" alignContent="center" alignItems='center'>
                        <FormControlLabel control={<Switch checked={state.funnel.useForSupport ?? false}
                                                           onChange={e => setState(prevState => ({
                                                               ...prevState, funnel: {...prevState.funnel, useForSupport: e.target.checked}
                                                           }))}/>}
                                          label="Use for AI support offers"/>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Betty creates personal offers through it (one per platform); events never trigger it.
                        </Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="rewardConfiguration">
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>Reward configuration</Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={rewardConfiguration}
                            onChange={(_, value: RewardConfiguration | null) => {
                                if (value !== null) {
                                    setRewardConfiguration(value);
                                }
                            }}
                        >
                            <ToggleButton value="STATIC">Static reward</ToggleButton>
                            <ToggleButton value="DYNAMIC">Dynamic reward</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="dailyLimit" alignContent="center" alignItems='center'>
                        <TextField label="Daily Limit" type="number" variant="filled"
                                   value={state.funnel.dailyLimit} fullWidth={true}
                                   onChange={e => {
                                       const value = parseInt(e.target.value);
                                       setState(prevState => ({
                                           ...prevState, funnel: {...prevState.funnel, dailyLimit: value}
                                       }));
                                   }}/>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="userPurchaserType">
                        <FormControl fullWidth>
                            <InputLabel id="user-purchaser-type-label">{"User purchaser type"}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="user-purchaser-type-label"
                                id="user-purchaser-type-select"
                                value={state.funnel.userPurchaserType ?? 'ALL'}
                                onChange={e => onUserPurchaserTypeSelected(e.target.value)}
                                label="User purchaser type">
                                <MenuItem value="ALL">All</MenuItem>
                                <MenuItem value="NOT_PURCHASER">Not Purchaser</MenuItem>
                                <MenuItem value="PURCHASER">Purchaser</MenuItem>
                                <MenuItem value="BIG_FISH">Big Fish</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {isStaticReward && (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="customBetcoins" alignContent="center" alignItems='center'>
                                <TextField label="Custom Betcoins (0 - 1,000,000,000)" type="number" variant="filled"
                                           value={state.funnel.customBetcoins ?? ''} fullWidth={true}
                                           required={isStaticReward}
                                           error={isStaticReward && state.funnel.customBetcoins == null}
                                           helperText={isStaticReward && state.funnel.customBetcoins == null
                                               ? 'Required for static rewards.'
                                               : 'Required for static rewards.'}
                                           onChange={e => {
                                               const value = e.target.value === '' ? null : parseInt(e.target.value);
                                               setState(prevState => ({
                                                   ...prevState, funnel: {...prevState.funnel, customBetcoins: value}
                                               }));
                                           }}
                                           inputProps={{step: 1, min: 0, max: 1000000000}}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 0.75,
                                        display: 'block',
                                        fontWeight: 700,
                                        color: getAttractivityColor(staticOfferAttractivity?.verdict),
                                    }}
                                >
                                    {staticOfferAttractivityLoading
                                        ? 'Evaluating offer value vs shop...'
                                        : staticOfferAttractivity != null && formattedAttractivity != null
                                            ? `Value vs shop: ${formattedAttractivity} · ${staticOfferAttractivity.verdict} ${staticOfferAttractivity.emoji}`
                                            : 'Enter betcoins and pricing tier to evaluate static offer value.'}
                                </Typography>
                            </Grid>
                        </React.Fragment>
                    )}
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="minTier">
                        <FormControl fullWidth>
                            <InputLabel id="min-tier-label">{`Select ${pricingTierLabel.toLowerCase()}`}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="min-tier-label"
                                id="min-tier-simple-select"
                                value={state.funnel.minTier ?? ''}
                                onChange={e => onMinTierSelected(e.target.value)}
                                label={pricingTierLabel}>
                                {state.options.tiers.map(x => <MenuItem key={x.tier}
                                                                        value={x.tier}>{x.tier} ({x.priceInUSD} $)</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{color: 'info.main', mt: 0.5, display: 'block'}}>
                            {pricingTierHint}
                        </Typography>
                    </Grid>
                    {isStaticReward && (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="oldPriceTier">
                                <FormControl fullWidth>
                                    <InputLabel id="old-price-tier-label">Old price tier</InputLabel>
                                    <Select
                                        fullWidth={true}
                                        labelId="old-price-tier-label"
                                        id="old-price-tier-select"
                                        value={state.funnel.oldPriceTier ?? ''}
                                        onChange={e => onOldPriceTierSelected(e.target.value)}
                                        label="Old price tier">
                                        <MenuItem value="">Automatic</MenuItem>
                                        {state.options.tiers.map(x => <MenuItem key={x.tier}
                                                                                value={x.tier}>{x.tier} ({x.priceInUSD} $)</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" sx={{color: 'info.main', mt: 0.5, display: 'block'}}>
                                    Optional. Leave empty to let the server use the +1 price tier as default old price for the selected pricing tier.
                                </Typography>
                            </Grid>
                        </React.Fragment>
                    )}
                    {isStaticReward && (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2}>
                                <Typography variant="caption" sx={{color: 'success.main', fontWeight: 700, display: 'block'}}>
                                    {discountLoading
                                        ? 'Calculating approximate discount...'
                                        : formattedDiscount != null
                                            ? `Approximate discount: ${formattedDiscount}`
                                            : 'Approximate discount is unavailable until a pricing tier is selected.'}
                                </Typography>
                            </Grid>
                        </React.Fragment>
                    )}
                    {!isStaticReward && (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="bonusSize" alignContent="center" alignItems='center'>
                                <TextField label="bonus in %" type="number" variant="filled"
                                           value={Math.round(Math.round(state.funnel.bonusSize * 100))} fullWidth={true}
                                           onChange={e => {
                                               const value = parseInt(e.target.value);
                                               setState(prevState => ({
                                                   ...prevState, funnel: {...prevState.funnel, bonusSize: value / 100.0}
                                               }));
                                           }}/>
                            </Grid>
                        </React.Fragment>
                    )}
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Funnel frequency:</Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="lifetime">
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">{"Select offer lifetime"}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="banner-simple-select-label"
                                id="pur-tier-simple-select"
                                value={state.funnel.lifetime}
                                onChange={e => onOfferLifetimeSelected(e.target.value)}
                                label="FunnelType">
                                {state.options.scopes.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="cooldown">
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">{"Select offer cooldown time"}</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="banner-simple-select-label"
                                id="pur-tier-simple-select"
                                value={state.funnel.cooldownTime}
                                onChange={e => onCooldowntimeSelected(e.target.value)}
                                label="FunnelType">
                                {state.options.scopes.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="activationEvents">
                        <FormControl fullWidth>
                            <InputLabel id="demo-multiple-checkbox-label">Activation events</InputLabel>
                            <Select
                                fullWidth={true}
                                labelId="demo-multiple-checkbox-label"
                                id="demo-multiple-checkbox"
                                multiple
                                value={state.funnel.activationEvents}
                                onChange={handleActivationEventsChange}
                                input={<OutlinedInput label="Activation events"/>}
                                renderValue={(selected) => selected.join(', ')}
                                MenuProps={MenuProps}
                            >
                                {state.options.activationEvents.map((name) => (<MenuItem key={name} value={name}>
                                    <Checkbox
                                        checked={(state.funnel.activationEvents?.indexOf(name) ?? -1) > -1}/>
                                    <ListItemText primary={name}/>
                                </MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Device restrictions:</Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="osTypes">
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>Platform</Typography>
                        <ToggleButtonGroup
                            exclusive
                            value={selectedOsType}
                            onChange={(_, value) => {
                                if (value !== null) {
                                    setState(prevState => ({...prevState, funnel: {...prevState.funnel, osTypes: [value]}}));
                                }
                            }}
                        >
                            <ToggleButton value="android" aria-label="Android">
                                <AndroidIcon sx={{mr: 1}}/> Android
                            </ToggleButton>
                            <ToggleButton value="ios" aria-label="iOS">
                                <Box sx={{mr: 1, display: 'flex', alignItems: 'center'}}><IosLogo/></Box> iOS
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="minBuildVersion">
                        <Autocomplete
                            freeSolo
                            clearOnEscape
                            options={minBuildVersionOptions}
                            loading={appVersionsLoading}
                            value={getSelectedBuildVersionOption(state.funnel.minBuildNumber)}
                            inputValue={minBuildNumberInput}
                            onChange={(_, value) => {
                                if (typeof value === 'string') {
                                    applyBuildVersionInput('minBuildNumber', value);
                                    return;
                                }
                                applyBuildVersionInput('minBuildNumber', value != null ? String(value.buildNumber) : '');
                            }}
                            onInputChange={(_, value, reason) => {
                                if (reason !== 'reset') {
                                    applyBuildVersionInput('minBuildNumber', value);
                                }
                            }}
                            getOptionLabel={(option) => typeof option === 'string' ? option : String(option.buildNumber)}
                            isOptionEqualToValue={(option, value) => option.buildNumber === value.buildNumber}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    {option.buildVersion} ({option.buildNumber})
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Min build version"
                                    variant="filled"
                                    fullWidth
                                    error={minBuildVersionError}
                                    helperText={minBuildVersionError ? 'Value should be between 1 and 999,999,999 and not exceed max build version' : 'Optional. Type a build number or pick one from the list.'}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {appVersionsLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="maxBuildVersion">
                        <Autocomplete
                            freeSolo
                            clearOnEscape
                            options={maxBuildVersionOptions}
                            loading={appVersionsLoading}
                            value={getSelectedBuildVersionOption(state.funnel.maxBuildNumber)}
                            inputValue={maxBuildNumberInput}
                            onChange={(_, value) => {
                                if (typeof value === 'string') {
                                    applyBuildVersionInput('maxBuildNumber', value);
                                    return;
                                }
                                applyBuildVersionInput('maxBuildNumber', value != null ? String(value.buildNumber) : '');
                            }}
                            onInputChange={(_, value, reason) => {
                                if (reason !== 'reset') {
                                    applyBuildVersionInput('maxBuildNumber', value);
                                }
                            }}
                            getOptionLabel={(option) => typeof option === 'string' ? option : String(option.buildNumber)}
                            isOptionEqualToValue={(option, value) => option.buildNumber === value.buildNumber}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    {option.buildVersion} ({option.buildNumber})
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Max build version"
                                    variant="filled"
                                    fullWidth
                                    error={maxBuildVersionError}
                                    helperText={maxBuildVersionError ? 'Value should be between 1 and 999,999,999 and not be lower than min build version' : 'Optional. Type a build number or pick one from the list.'}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {appVersionsLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                            {deviceRestrictionsHint}
                        </Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">User tag targeting:</Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Autocomplete
                            multiple
                            options={availableUserTags}
                            value={selectedUserTagOptions}
                            isOptionEqualToValue={(option, value) => option.tagName === value.tagName}
                            getOptionLabel={(option) => option.tagName}
                            onChange={(_, value) => {
                                setState(prevState => ({
                                    ...prevState,
                                    funnel: {
                                        ...prevState.funnel,
                                        userTags: value.map(tag => tag.tagName)
                                    }
                                }));
                            }}
                            fullWidth
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2">{option.tagName}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.approximateUsersCount == null
                                                ? 'Approx. users: counting...'
                                                : `Approx. users: ${option.approximateUsersCount.toLocaleString()}`}
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.tagName}
                                        label={option.tagName}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Target user tags" placeholder="Optional" />
                            )}
                        />
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <FormControl fullWidth disabled={state.funnel.userTags.length === 0}>
                            <InputLabel id="funnel-user-tag-combination-mode-label">Tag combination</InputLabel>
                            <Select
                                labelId="funnel-user-tag-combination-mode-label"
                                value={state.funnel.userTagCombinationMode}
                                label="Tag combination"
                                onChange={(event) => {
                                    setState(prevState => ({
                                        ...prevState,
                                        funnel: {
                                            ...prevState.funnel,
                                            userTagCombinationMode: event.target.value as UserTagCombinationMode
                                        }
                                    }));
                                }}
                            >
                                {state.options.userTagCombinationModes.map(mode => (
                                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                            {state.funnel.userTags.length === 0
                                ? 'No user tags selected. Funnel keeps current backward-compatible behavior and targets users regardless of tags.'
                                : `Users must match ${state.funnel.userTagCombinationMode} of the selected tags.`}
                        </Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Funnel localization:</Typography>
                    </Grid>
                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="templateModeToggle">
                        <ToggleButtonGroup
                            value={templateMode}
                            exclusive
                            onChange={(_, newMode) => { if (newMode !== null) setTemplateMode(newMode); }}
                            fullWidth
                            color="primary"
                        >
                            <ToggleButton value="legacy">Legacy Template</ToggleButton>
                            <ToggleButton value="html">HTML Template</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {templateMode === 'legacy' ? (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="localizationTemplate">
                                <FormControl fullWidth>
                                    <InputLabel id="demo-simple-select-label">{"Select localization template"}</InputLabel>
                                    <Select
                                        fullWidth={true}
                                        labelId="banner-simple-select-label"
                                        id="pur-tier-simple-select"
                                        value={state.funnel.localizationTemplate}
                                        onChange={e => onLocalizationTemplateSelected(e.target.value)}
                                        label="FunnelType">
                                        {state.templates.map(x => <MenuItem key={x.id} value={x.id}>{x.id}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={1} lg={2} key="editLocalizations">
                                <Button fullWidth={true} style={{height: '54px'}} onClick={() => editLocalizations()}>Edit
                                    localizations</Button>
                            </Grid>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="localizationPreview">
                                <Card sx={{width: '100%'}}>
                                    <CardContent>
                                        <img
                                            src={state.templates.find(x => x.id == state.funnel.localizationTemplate)?.localizations['en']?.iconUrl ?? ''}
                                            width={160} height={128}
                                            style={{objectFit: 'contain'}}/>
                                        <Typography gutterBottom variant="h6" component="div">
                                            Title: {state.templates.find(x => x.id == state.funnel.localizationTemplate)?.localizations['en']?.title ?? ''}
                                        </Typography>
                                        <Typography gutterBottom variant="body1" component="div">
                                            Subtitle: {state.templates.find(x => x.id == state.funnel.localizationTemplate)?.localizations['en']?.subtitle ?? ''}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={2} key="htmlTemplate">
                                <FormControl fullWidth>
                                    <InputLabel id="html-template-select-label">{"Select HTML template"}</InputLabel>
                                    <Select
                                        fullWidth={true}
                                        labelId="html-template-select-label"
                                        id="html-template-select"
                                        value={state.funnel.htmlTemplateId || ''}
                                        onChange={e => setState(prevState => ({
                                            ...prevState,
                                            funnel: {...prevState.funnel, htmlTemplateId: e.target.value || null}
                                        }))}
                                        label="HTML Template">
                                        {state.htmlTemplates
                                            .filter(x => (x.osType || 'android') === (state.funnel.osTypes?.[0] ?? 'android'))
                                            .map(x => (
                                                <MenuItem key={x.id} value={x.id}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        {x.osType === 'ios'
                                                            ? <IosLogo size={16}/>
                                                            : <AndroidIcon sx={{fontSize: 16, color: '#3ddc84'}}/>
                                                        }
                                                        <Chip
                                                            label={x.isValid ? 'Valid' : 'Invalid'}
                                                            size="small"
                                                            color={x.isValid ? 'success' : 'error'}
                                                            variant="filled"
                                                            sx={{height: 18, fontSize: '0.65rem'}}
                                                        />
                                                        {x.name}
                                                    </Box>
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={2}/>
                            <Grid item xs={1} lg={2} key="editHtmlTemplates">
                                <Button fullWidth={true} style={{height: '54px'}} onClick={() => navigate('/funnels/html-templates')}>Manage
                                    HTML templates</Button>
                            </Grid>
                            {state.funnel.htmlTemplateId && (() => {
                                const selectedHtmlTemplate = state.htmlTemplates.find(x => x.id === state.funnel.htmlTemplateId);
                                if (!selectedHtmlTemplate) return null;
                                return (
                                    <React.Fragment>
                                        <Box width="100%"/>
                                        <Grid item xs={0} sm={0} md={1} lg={2}/>
                                        <Grid item xs={2} key="htmlTemplatePreview">
                                            <Card sx={{width: '100%'}}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        {selectedHtmlTemplate.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                                        Localization keys: {selectedHtmlTemplate.localizations.length}
                                                    </Typography>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1}}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Preview language:
                                                        </Typography>
                                                        <Select
                                                            size="small"
                                                            value={previewLanguage}
                                                            onChange={e => setPreviewLanguage(e.target.value as string)}
                                                            sx={{minWidth: 80}}
                                                        >
                                                            {SUPPORTED_LANGUAGES.map(lang => (
                                                                <MenuItem key={lang} value={lang}>{lang.toUpperCase()}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </Box>
                                                    <Box sx={{mt: 1}}>
                                                        <HtmlTemplatePreview
                                                            htmlContent={selectedHtmlTemplate.htmlContent}
                                                            localizations={selectedHtmlTemplate.localizations}
                                                            language={previewLanguage}
                                                            betcoinsMacrosKey={selectedHtmlTemplate.betcoinsMacrosKey}
                                                            expirationTimestampMacrosKey={selectedHtmlTemplate.expirationTimestampMacrosKey}
                                                            discountMacroKey={selectedHtmlTemplate.discountMacroKey}
                                                            oldPriceKeys={selectedHtmlTemplate.oldPriceKeys}
                                                            newPriceKeys={selectedHtmlTemplate.newPriceKeys}
                                                            width={375}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </React.Fragment>
                                );
                            })()}
                        </React.Fragment>
                    )}

                    {/*region Elements*/}
                    <Box width="100%"/>
                    <Grid item md={1} lg={2}/>
                    <Grid item xs={2}>
                        <Typography variant="h6">Funnel elements:</Typography>
                    </Grid>

                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="elements">
                        {state.funnel.elements?.length > 0 && state.funnel.elements.map((element, index) => (
                            <React.Fragment key={element.id}>
                                <Box height="20px" width="100%"/>
                                <FormControl fullWidth>
                                    <TextField
                                        label="ID"
                                        variant="filled"
                                        value={state.funnelElementTypes.find(el => el === element.type) ? element.id : ''}
                                        disabled
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id={`element-type-label-${index}`}>Type</InputLabel>
                                    <Select
                                        labelId={`element-type-label-${index}`}
                                        value={state.funnelElementTypes.find(el => el === element.type) ? element.type : ''}
                                        onChange={e => {
                                            const value = e.target.value as string;
                                            setState(prevState => ({
                                                ...prevState, funnel: {
                                                    ...prevState.funnel,
                                                    elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                        ...el, type: value
                                                    } : el)
                                                }
                                            }));
                                        }}
                                        variant="filled"
                                    >
                                        {state.funnelElementTypes.map(x => <MenuItem key={x}
                                                                                     value={x}>{x}</MenuItem>)}
                                    </Select>
                                </FormControl>

                                {renderElementSpecificFields(element, index)}

                                {!showBiasControls && (<>
                                    <Box width="100%"/>
                                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                                    <Grid item xs={2} key="addBias">
                                        <Button fullWidth={true} style={{height: '54px'}}
                                                onClick={() => {
                                                    setShowBiasControls(true);
                                                }}>Add bias</Button>
                                    </Grid>
                                </>)}
                                {showBiasControls && (<>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="Vertical Bias"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.constraints.verticalBias}
                                            onChange={e => {
                                                const value = parseFloat(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, constraints: {
                                                                    ...el.layoutParams.constraints, verticalBias: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, max: 1, step: 0.1}}
                                        />
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="Horizontal Bias"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.constraints.horizontalBias}
                                            onChange={e => {
                                                const value = parseFloat(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, constraints: {
                                                                    ...el.layoutParams.constraints,
                                                                    horizontalBias: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, max: 1, step: 0.1}}
                                        />
                                    </FormControl>
                                </>)}

                                {!showMarginsControls && (<>
                                    <Box width="100%"/>
                                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                                    <Grid item xs={2} key="addMargins">
                                        <Button fullWidth={true} style={{height: '54px'}}
                                                onClick={() => {
                                                    setShowMarginsControls(true);
                                                }}>Add margins</Button>
                                    </Grid>
                                </>)}
                                {showMarginsControls && (<>
                                    <FormControl fullWidth sx={{width: '20%', margin: '10px'}}>
                                        <TextField
                                            label="Margin Top"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.margins.top}
                                            onChange={e => {
                                                const value = parseInt(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, margins: {
                                                                    ...el.layoutParams.margins, top: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, step: 1}}
                                        />
                                    </FormControl>


                                    <FormControl fullWidth sx={{width: '20%', margin: '10px'}}>
                                        <TextField
                                            label="Margin Bottom"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.margins.bottom}
                                            onChange={e => {
                                                const value = parseInt(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, margins: {
                                                                    ...el.layoutParams.margins, bottom: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, step: 1}}
                                        />
                                    </FormControl>
                                    <FormControl fullWidth sx={{width: '20%', margin: '10px'}}>
                                        <TextField
                                            label="Margin Start"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.margins.start}
                                            onChange={e => {
                                                const value = parseInt(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, margins: {
                                                                    ...el.layoutParams.margins, start: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, step: 1}}
                                        />
                                    </FormControl>
                                    <FormControl fullWidth sx={{width: '20%', margin: '10px'}}>
                                        <TextField
                                            label="Margin End"
                                            type="number"
                                            variant="filled"
                                            value={element.layoutParams.margins.end}
                                            onChange={e => {
                                                const value = parseInt(e.target.value);
                                                setState(prevState => ({
                                                    ...prevState, funnel: {
                                                        ...prevState.funnel,
                                                        elements: prevState.funnel.elements.map((el, i) => i === index ? {
                                                            ...el, layoutParams: {
                                                                ...el.layoutParams, margins: {
                                                                    ...el.layoutParams.margins, end: value
                                                                }
                                                            }
                                                        } : el) as FunnelConfigElementDto[]
                                                    }
                                                }));
                                            }}
                                            inputProps={{min: 0, step: 1}}
                                        />
                                    </FormControl>
                                </>)}
                                {constraints.slice(index * 4, (index + 1) * 4).map((constraint, c_index) => {
                                    const constraint_index = c_index + 4 * index;
                                    return (<Box key={constraint_index} display="flex" gap="10px">
                                        <FormControl fullWidth>
                                            <InputLabel id={`constraint1-label-${index}-${constraint_index}`}>Constraint
                                                1</InputLabel>
                                            <Select
                                                labelId={`constraint1-label-${index}-${constraint_index}`}
                                                value={constraints[constraint_index]?.constraint1 || ''}
                                                onChange={e => handleConstraint1Change(constraint_index, e.target.value, index)}
                                            >
                                                {availableOptions.map(option => (<MenuItem key={option} value={option}
                                                                                           disabled={constraints.slice(index * 4, (index + 1) * 4).map(c => c.constraint1).includes(option)}>
                                                    {option}
                                                </MenuItem>))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel id={`constraint2-label-${constraint_index}`}>Constraint
                                                2</InputLabel>
                                            <Select
                                                labelId={`constraint2-label-${constraint_index}`}
                                                value={constraints[constraint_index]?.constraint2 || ''}
                                                onChange={e => handleConstraint2Change(constraint_index, e.target.value)}
                                                disabled={!constraints[constraint_index]?.constraint1}
                                            >
                                                {getConstraint2Options(constraints[constraint_index]?.constraint1 || '').map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel id={`start-to-end-id-label-${index}-${constraint_index}`}>Start
                                                to End ID</InputLabel>
                                            <Select
                                                labelId={`start-to-end-id-label-${index}-${constraint_index}`}
                                                value={constraints.length > 0 ? element.layoutParams.constraints[`${constraints[constraint_index].constraint1.toLowerCase()}To${constraints[constraint_index].constraint2.charAt(0).toUpperCase()}${constraint.constraint2.slice(1).toLowerCase()}Id`] : ''}
                                                onChange={e => {
                                                    updateElementConstraint(index, constraints[constraint_index].constraint1, constraints[constraint_index].constraint2, e.target.value as string);
                                                }}
                                                variant="filled"
                                            >
                                                <MenuItem key="" value=""> </MenuItem>
                                                <MenuItem key="parent" value="parent">Parent</MenuItem>
                                                {state.funnel.elements
                                                    .filter(el => el.id !== element.id)
                                                    .map((el) => (
                                                        <MenuItem key={el.id} value={el.id}>{el.type}</MenuItem>))}
                                            </Select>
                                        </FormControl>
                                    </Box>);
                                })}
                            </React.Fragment>))}
                    </Grid>

                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={2} key="addElement">
                        <Button fullWidth={true} style={{height: '54px'}} onClick={() => addElements()}>Add new
                            element</Button>
                    </Grid>
                    {/*endregion*/}

                    <Box width="100%"/>
                    <Grid item xs={0} sm={0} md={1} lg={2}/>
                    <Grid item xs={isEditMode ? 1 : 2} key="addMatchContainer">
                        <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                                onClick={() => onCreateClicked()}>{isEditMode ? "Update funnel" : "Add funnel"}</Button>
                    </Grid>
                    {isEditMode ? <Grid item xs={1} key="deleteMatchContainer">
                        <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                                onClick={() => onDeleteClicked()}>Delete funnel</Button>
                    </Grid> : <div/>}
                </React.Fragment>}
            </Grid>

        </Stack>
        <Dialog
            open={state.dialogOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                Error
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {state.dialogMsg}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDialogClose}>Got it</Button>
            </DialogActions>
        </Dialog>
    </div>)

}
